import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../utils/supabase';
import { Button, FormField, Input, NumberInput, Select, Switch, Textarea } from '../common';
import { useSeason } from '../../context/SeasonContext';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import {
  doublesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionSelectValue,
  isSinglesFormat,
} from '../../utils/competitiveMatchFormat';
import {
  DEFAULT_SCORING,
} from '../../utils/gameScoreRules';
import {
  getSelectableSessionTypes,
  isPegBoardSession,
  isSelectableSessionType,
} from '../../utils/sessionTypeUtils';
import {
  PEG_BOARD_QUEUE_RETURN_OPTIONS,
  PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  PEG_BOARD_SELECTION_WINDOW_DEFAULT,
  PEG_BOARD_SELECTION_WINDOW_OPTIONS,
  clampPegBoardSelectionWindow,
  normalizePegBoardQueueReturn,
  pegBoardPersistFields,
} from '../../utils/pegBoard/constants';

export const formatStartNowSessionName = (date = new Date()) => format(date, 'EEE d MMM');

const emptyFormData = () => ({
  sessionName: formatStartNowSessionName(),
  numberOfCourts: '',
  endTime: '',
  sessionTypeId: '',
  location: '',
  maxPlayers: '',
  description: '',
  game_type_restriction: 'balanced_only',
  enable_player_categorisation: false,
  competitive_match_format: 'doubles',
  points_target: DEFAULT_SCORING.pointsTarget,
  deuce_mode: DEFAULT_SCORING.deuceMode,
  peg_board_selection_window: PEG_BOARD_SELECTION_WINDOW_DEFAULT,
  peg_board_queue_return: PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
});

const parseOptionalPositiveInt = (value) => {
  if (value === '' || value == null) return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
};

const CustomSessionConfigModal = ({
  isOpen,
  onClose,
  onStartSession,
  selectedClub,
  currentUser,
}) => {
  const { season } = useSeason();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [sessionSetups, setSessionSetups] = useState([]);
  const [selectedSetupId, setSelectedSetupId] = useState('');

  const [formData, setFormData] = useState(emptyFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [optionalExpanded, setOptionalExpanded] = useState(false);

  const fetchSessionTypes = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('session_types')
        .select('*')
        .order('name');

      if (fetchError) throw fetchError;
      setSessionTypes(getSelectableSessionTypes(data || []));
    } catch (err) {
      console.error('Error fetching session types:', err);
      setError('Failed to load session types');
    }
  }, []);

  const fetchSessionSetups = useCallback(async () => {
    if (!selectedClub?.id) return;
    try {
      const { data, error: fetchError } = await supabase
        .from('club_session_setup')
        .select(`
          *,
          session_types!inner(name, description)
        `)
        .eq('club_id', selectedClub.id)
        .eq('is_active', true)
        .order('session_name');

      if (fetchError) throw fetchError;
      setSessionSetups(
        (data || []).filter((setup) => isSelectableSessionType(setup.session_types))
      );
    } catch (err) {
      console.error('Error fetching session setups:', err);
    }
  }, [selectedClub?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setOptionalExpanded(false);
    setShowValidationSummary(false);
    setError(null);
    setSelectedSetupId('');
    setFormData(emptyFormData());
    fetchSessionTypes();
    fetchSessionSetups();
  }, [isOpen, fetchSessionTypes, fetchSessionSetups]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const applySetup = (setup) => {
    if (!setup) return;
    setFormData((prev) => ({
      ...prev,
      sessionTypeId: setup.session_type_id || prev.sessionTypeId,
      game_type_restriction: setup.game_type_restriction || 'balanced_only',
      enable_player_categorisation: setup.enable_player_categorisation || false,
      competitive_match_format: setup.competitive_match_format || 'doubles',
      points_target: setup.points_target ?? DEFAULT_SCORING.pointsTarget,
      deuce_mode: setup.deuce_mode || DEFAULT_SCORING.deuceMode,
      peg_board_selection_window: setup.peg_board_selection_window ?? PEG_BOARD_SELECTION_WINDOW_DEFAULT,
      peg_board_queue_return: normalizePegBoardQueueReturn(setup.peg_board_queue_return),
    }));
  };

  const evaluateRequiredFields = useCallback((data) => {
    const errors = {};
    const missing = [];

    const addError = (key, label, message) => {
      errors[key] = message || `${label} is required`;
      if (!missing.includes(label)) {
        missing.push(label);
      }
    };

    if (!data.sessionName || data.sessionName.trim() === '') {
      addError('sessionName', 'Session Name');
    }

    if (!data.sessionTypeId) {
      addError('sessionTypeId', 'Session Type');
    }

    return { errors, missing };
  }, []);

  useEffect(() => {
    const { errors, missing } = evaluateRequiredFields(formData);
    setFieldErrors(errors);
    setMissingFields(missing);
  }, [formData, evaluateRequiredFields]);

  const sessionTypeOptions = sessionTypes.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  const selectedSessionType = sessionTypes.find((t) => t.id === formData.sessionTypeId);
  const isCompetitive = selectedSessionType?.name === 'Competitive Session';
  const isPegBoard = isPegBoardSession(selectedSessionType);
  const hasOptionalValues = Boolean(
    formData.location?.trim()
    || formData.maxPlayers !== ''
    || formData.description?.trim()
    || formData.numberOfCourts !== ''
    || formData.endTime
    || (isCompetitive
      && !isSinglesFormat(formData.competitive_match_format)
      && formData.enable_player_categorisation),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { errors, missing } = evaluateRequiredFields(formData);
    setFieldErrors(errors);
    setMissingFields(missing);

    if (missing.length > 0) {
      setShowValidationSummary(true);
      return;
    }

    setShowValidationSummary(false);

    setLoading(true);
    setError(null);

    try {
      const sessionData = {
        clubId: selectedClub.id,
        name: formData.sessionName.trim(),
        description: formData.description || `Custom session at ${selectedClub.name}`,
        createdById: currentUser.id,
        seasonId: season?.id,
        numberOfCourts: parseOptionalPositiveInt(formData.numberOfCourts),
        endTime: formData.endTime || null,
        sessionTypeId: formData.sessionTypeId,
        location: formData.location || null,
        maxPlayers:
          formData.maxPlayers !== '' && formData.maxPlayers != null
            ? parseInt(formData.maxPlayers, 10)
            : null,
        game_type_restriction: formData.game_type_restriction,
        enable_player_categorisation:
          isSinglesFormat(formData.competitive_match_format) ? false : formData.enable_player_categorisation,
        competitive_match_format: isPegBoard ? 'doubles' : (formData.competitive_match_format || 'doubles'),
        points_target: formData.points_target ?? DEFAULT_SCORING.pointsTarget,
        deuce_mode: formData.deuce_mode || DEFAULT_SCORING.deuceMode,
        ...pegBoardPersistFields(formData),
      };

      onStartSession(sessionData, null);
      onClose();
    } catch (err) {
      console.error('Error creating custom session:', err);
      setError('Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border-medium px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-text-primary">Start new session</h2>
            <p className="mt-1 text-sm text-text-secondary">
              Price, end time, and location can be added when you wrap up.
            </p>
          </div>
          <ModalCloseButton onClose={onClose} className="sticky top-0" />
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain touch-pan-y px-4 py-4 sm:px-6">
            {error && (
              <div className="rounded-md border border-danger bg-danger-light p-3 text-danger-dark">
                <p className="text-sm">{error}</p>
              </div>
            )}

          {sessionSetups.length > 0 && (
            <FormField
              label="Use a saved setup (optional)"
              helperText="Fills session type and matchmaking only. Courts and times stay blank."
            >
              <Select
                value={selectedSetupId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedSetupId(nextId);
                  const setup = sessionSetups.find((s) => s.id === nextId);
                  applySetup(setup);
                }}
                placeholder="Choose a session setup..."
                options={sessionSetups.map((setup) => ({
                  value: setup.id,
                  label: setup.session_name,
                }))}
              />
            </FormField>
          )}

          <FormField label="Session Name" required error={fieldErrors.sessionName}>
            <Input
              type="text"
              value={formData.sessionName}
              onChange={(e) => handleInputChange('sessionName', e.target.value)}
              placeholder="Enter session name"
              error={!!fieldErrors.sessionName}
            />
          </FormField>

          <FormField label="Session Type" required error={fieldErrors.sessionTypeId}>
            <Select
              value={formData.sessionTypeId}
              onChange={(e) => {
                const nextId = e.target.value;
                const nextType = sessionTypes.find((t) => t.id === nextId);
                setFormData((prev) => ({
                  ...prev,
                  sessionTypeId: nextId,
                  ...(isPegBoardSession(nextType) ? { competitive_match_format: 'doubles' } : {}),
                }));
              }}
              placeholder="Select session type..."
              options={sessionTypeOptions}
              error={!!fieldErrors.sessionTypeId}
            />
          </FormField>

          {isPegBoard && (
            <>
              <FormField
                label="Picker can choose from"
                helperText="Includes the picker. Must be at least 4 for doubles."
              >
                <Select
                  value={String(clampPegBoardSelectionWindow(formData.peg_board_selection_window))}
                  onChange={(e) => handleInputChange('peg_board_selection_window', parseInt(e.target.value, 10))}
                  options={PEG_BOARD_SELECTION_WINDOW_OPTIONS}
                />
              </FormField>
              <FormField
                label="Queue return order"
                helperText="How the four players go back onto the queue after a score is entered"
              >
                <Select
                  value={normalizePegBoardQueueReturn(formData.peg_board_queue_return)}
                  onChange={(e) => handleInputChange('peg_board_queue_return', e.target.value)}
                  options={PEG_BOARD_QUEUE_RETURN_OPTIONS}
                />
              </FormField>
            </>
          )}

          {isCompetitive && (
            <FormField label="Match format">
              <Select
                value={formData.competitive_match_format || 'doubles'}
                onChange={(e) => {
                  const next = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    competitive_match_format: next,
                    ...(isSinglesFormat(next) ? { enable_player_categorisation: false } : {}),
                  }));
                }}
                options={[
                  { value: 'doubles', label: 'Doubles' },
                  { value: 'singles', label: 'Singles' },
                ]}
              />
            </FormField>
          )}

          {isCompetitive && (
            <FormField label="Game Type Settings">
              <Select
                value={
                  isSinglesFormat(formData.competitive_match_format)
                    ? singlesGameTypeRestrictionSelectValue(formData.game_type_restriction)
                    : formData.game_type_restriction
                }
                onChange={(e) => handleInputChange('game_type_restriction', e.target.value)}
                options={
                  isSinglesFormat(formData.competitive_match_format)
                    ? singlesGameTypeRestrictionOptions
                    : doublesGameTypeRestrictionOptions
                }
              />
            </FormField>
          )}

          {formData.sessionTypeId ? (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-text-primary">Game scoring</p>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Points to win">
                <Select
                  value={String(formData.points_target ?? DEFAULT_SCORING.pointsTarget)}
                  onChange={(e) => handleInputChange('points_target', parseInt(e.target.value, 10))}
                  options={[
                    { value: '21', label: '21 points' },
                    { value: '15', label: '15 points' },
                  ]}
                />
              </FormField>
              <FormField label="Deuce rules">
                <Select
                  value={formData.deuce_mode || DEFAULT_SCORING.deuceMode}
                  onChange={(e) => handleInputChange('deuce_mode', e.target.value)}
                  options={[
                    { value: 'standard', label: 'Standard' },
                    { value: 'no_deuce', label: 'No deuce' },
                  ]}
                />
              </FormField>
            </div>
          </div>
          ) : null}

          <div className="overflow-hidden rounded-lg border border-border-medium">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-surface-subtle"
              onClick={() => setOptionalExpanded((prev) => !prev)}
              aria-expanded={optionalExpanded}
            >
              <div className="min-w-0">
                <span className="text-sm font-semibold text-text-primary">Optional details</span>
                {!optionalExpanded && hasOptionalValues ? (
                  <p className="mt-0.5 truncate text-xs text-text-secondary">Added</p>
                ) : null}
              </div>
              <svg
                className={`h-5 w-5 shrink-0 text-text-muted transition-transform duration-200 ${optionalExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {optionalExpanded ? (
              <div className="space-y-4 border-t border-border-medium px-4 py-4">
                {isCompetitive && !isSinglesFormat(formData.competitive_match_format) && (
                  <div className="form-group">
                    <div className="flex items-center justify-between gap-4">
                      <label id="custom-session-categorisation-label" className="form-label mb-0">
                        Enable Player Categorisation
                      </label>
                      <Switch
                        id="custom-session-categorisation"
                        name="enable_player_categorisation"
                        checked={formData.enable_player_categorisation}
                        onChange={(e) => handleInputChange('enable_player_categorisation', e.target.checked)}
                        aria-labelledby="custom-session-categorisation-label"
                      />
                    </div>
                  </div>
                )}

                <FormField
                  label="Number of Courts"
                  helperText="Set courts on the live session if you leave this blank."
                >
                  <NumberInput
                    value={formData.numberOfCourts}
                    onChange={(e) => handleInputChange('numberOfCourts', e.target.value)}
                    min={1}
                    max={20}
                    placeholder="Set on court layout"
                    allowEmpty
                    inputMode="numeric"
                  />
                </FormField>

                <FormField
                  label="End Time"
                  helperText="Can be filled in on wrap-up."
                >
                  <Input
                    type="time"
                    step={60}
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                  />
                </FormField>

                <FormField label="Location">
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., Main Hall, Court 1-2"
                  />
                </FormField>

                <FormField label="Max Players">
                  <Input
                    type="number"
                    min={4}
                    max={32}
                    value={formData.maxPlayers}
                    onChange={(e) => handleInputChange('maxPlayers', e.target.value)}
                    placeholder="No limit"
                  />
                </FormField>

                <FormField label="Description">
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Additional session details..."
                    rows={3}
                  />
                </FormField>
              </div>
            ) : null}
          </div>

            {showValidationSummary && missingFields.length > 0 && (
              <div className="rounded-md border border-danger bg-danger-light p-3 text-sm text-danger-dark">
                Please complete: {missingFields.join(', ')}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-border-medium px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:px-6">
            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={loading || missingFields.length > 0} variant="primary" className="min-w-[8rem] flex-1">
                {loading ? 'Starting...' : 'Start session'}
              </Button>

              <Button type="button" onClick={onClose} variant="outline" className="min-w-[8rem]">
                Cancel
              </Button>
            </div>

            {missingFields.length > 0 && (
              <p className="mt-2 text-xs text-danger-dark">Missing mandatory fields</p>
            )}
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default CustomSessionConfigModal;
